import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MapIcon,
  LocationIcon,
  ListIcon,
  AdminIcon,
  LoginIcon,
  LogoutIcon,
  SearchIcon,
  FilterIcon,
  CloseIcon,
  CheckIcon,
  ExpandMoreIcon,
  BookmarkIcon
} from '../Icons';

function Header({
  isAuthenticated,
  language,
  onLogout,
  viewMode,
  onViewModeChange,
  searchQuery,
  onSearchChange,
  onFilterClick,
  activeFilters,
  tags = [],
  selectedTags = [],
  onTagsChange,
  lands = [],
  showSavedOnly = false,
  onSavedToggle,
  priceMin = null,
  priceMax = null,
  onPriceRangeChange
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  // Mobile only — the desktop layout keeps categories/price/saved/admin as
  // separate controls in `controls` below; on mobile they're consolidated
  // into this one bottom sheet (see the "Filters" trigger button) to avoid
  // wrapping into a cluttered second row of bordered boxes.
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);

  const hasPriceFilter = priceMin != null || priceMax != null;
  const mobileActiveFilterCount = selectedTags.length + (hasPriceFilter ? 1 : 0) + (showSavedOnly ? 1 : 0);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const clearSearch = () => {
    onSearchChange('');
  };

  const isActive = (path) => location.pathname === path;

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleTagToggle = (tagTitle) => {
    if (!onTagsChange) return;

    const newSelectedTags = selectedTags.includes(tagTitle)
      ? selectedTags.filter(t => t !== tagTitle)
      : [...selectedTags, tagTitle];

    onTagsChange(newSelectedTags);
  };

  const clearAllTags = () => {
    if (onTagsChange) {
      onTagsChange([]);
    }
  };

  // Count properties for each tag
  const getTagCount = (tagId) => {
    if (!lands || lands.length === 0) {
      console.log('Header: No lands data available');
      return 0;
    }

    console.log('Header lands count:', lands.length);
    console.log('Sample land tags:', lands[0]?.tags);

    const count = lands.filter(land => {
      // Handle both array and JSON string formats
      let landTags = land.tags;
      if (typeof landTags === 'string') {
        try {
          landTags = JSON.parse(landTags);
        } catch (e) {
          return false;
        }
      }

      return Array.isArray(landTags) && landTags.some(tag => tag.id === tagId);
    }).length;

    console.log(`Tag ID ${tagId} count:`, count);
    return count;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showSearchFilter = location.pathname !== '/admin';

  // Logo stands alone — no auth button grouped with it (see layout note below).
  const logo = (
    <a
      href="https://www.derwazaq8.com"
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 px-2 py-1 hover:opacity-80 transition-opacity flex-shrink-0"
    >
      <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#ccbf9b] to-[#006892] p-1.5 flex items-center justify-center flex-shrink-0">
        <img
          src="/logo-no-bg.webp"
          alt="دروازة البحرية"
          className="h-full w-full object-contain"
        />
      </div>
      <span className="text-gray-900 font-semibold text-sm hidden sm:inline whitespace-nowrap">دروازة البحرية</span>
    </a>
  );

  const authButton = isAuthenticated ? (
    <button
      onClick={onLogout}
      type="button"
      className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg
        text-[11px] sm:text-xs font-medium
        transition-all duration-200
        bg-white text-brand border border-brand/30 hover:border-brand flex-shrink-0"
    >
      <LogoutIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      <span className="hidden sm:inline whitespace-nowrap">
        {language === 'ar' ? 'الخروج' : 'Logout'}
      </span>
    </button>
  ) : (
    <button
      onClick={() => handleNavigation('/login')}
      type="button"
      className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg
        text-[11px] sm:text-xs font-medium
        transition-all duration-200
        bg-white text-brand border border-brand/30 hover:border-brand flex-shrink-0"
    >
      <LoginIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      <span className="hidden sm:inline whitespace-nowrap">
        {language === 'ar' ? 'الدخول' : 'Login'}
      </span>
    </button>
  );

  // Everything besides the logo and the auth button: categories, search,
  // price chip, the Map/Finder/List view tabs, saved toggle, admin button.
  // Shared between the mobile and desktop layouts below so the markup (and
  // the dropdown's own open/close state) only exists in one place.
  const controls = (
    <>
      {/* Categories Dropdown */}
      {showSearchFilter && (
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={toggleDropdown}
            className="flex items-center gap-2 px-3 h-9 bg-white border border-brand/30 rounded-lg hover:border-brand focus:outline-none focus:border-brand transition-colors"
          >
            <FilterIcon className="w-4 h-4 text-brand" />
            <span className="text-[11px] sm:text-xs text-gray-700 whitespace-nowrap">
              {selectedTags.length > 0
                ? `${selectedTags.length} ${language === 'ar' ? 'محدد' : 'selected'}`
                : (language === 'ar' ? 'التصنيفات' : 'Categories')
              }
            </span>
            <ExpandMoreIcon className={`w-4 h-4 text-brand transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && tags.length > 0 && (
            <div className="fixed md:absolute top-[120px] md:top-full md:mt-2 left-4 right-4 md:left-auto md:right-0 md:w-72 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[calc(100vh-140px)] md:max-h-96 overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-3 flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-700">
                  {language === 'ar' ? 'اختر التصنيفات' : 'Select Categories'}
                </span>
                {selectedTags.length > 0 && (
                  <button
                    onClick={clearAllTags}
                    className="text-xs text-red-600 hover:text-red-700 font-medium"
                  >
                    {language === 'ar' ? 'مسح الكل' : 'Clear All'}
                  </button>
                )}
              </div>

              {/* Tags List */}
              <div className="p-2">
                {tags.filter(tag => tag.title !== 'All').map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => handleTagToggle(tag.title)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors ${
                      selectedTags.includes(tag.title) ? 'bg-yellow-50' : ''
                    }`}
                  >
                    {/* Checkbox */}
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      selectedTags.includes(tag.title)
                        ? 'bg-primary border-primary'
                        : 'border-gray-300'
                    }`}>
                      {selectedTags.includes(tag.title) && (
                        <CheckIcon className="w-3 h-3 text-white" />
                      )}
                    </div>

                    {/* Color Indicator */}
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />

                    {/* Tag Title */}
                    <span className="flex-1 text-left text-sm text-gray-700">
                      {tag.title}
                    </span>

                    {/* Count */}
                    <span className="text-xs text-gray-500 font-medium">
                      ({getTagCount(tag.id)})
                    </span>
                  </button>
                ))}
              </div>

              {/* Footer */}
              {selectedTags.length > 0 && (
                <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-3">
                  <div className="flex flex-wrap gap-1">
                    {selectedTags.map((tagTitle) => {
                      const tag = tags.find(t => t.title === tagTitle);
                      return (
                        <span
                          key={tagTitle}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-white"
                          style={{ backgroundColor: tag?.color || '#999' }}
                        >
                          {tagTitle}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTagToggle(tagTitle);
                            }}
                            className="hover:bg-white/20 rounded-full p-0.5"
                          >
                            <CloseIcon className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Search Box — sized to comfortably fit a 7-digit code/land number */}
      {showSearchFilter && (
        <div className="relative">
          <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand pointer-events-none" />
          <input
            type="text"
            placeholder={language === 'ar' ? 'الكود...' : 'Code...'}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className={`
              w-[100px] h-9
              pl-8 pr-2 py-2
              bg-white border border-brand/30 rounded-lg
              font-cairo text-sm
              focus:outline-none focus:border-brand
              hover:border-brand
              ${language === 'ar' ? 'text-right' : 'text-left'}
            `}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded-full"
            >
              <CloseIcon className="w-3.5 h-3.5 text-brand" />
            </button>
          )}
        </div>
      )}

      {hasPriceFilter && (
        <button
          onClick={() => onPriceRangeChange && onPriceRangeChange({ min: null, max: null })}
          className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-brand/10 text-brand text-[11px] font-medium"
        >
          {priceMin != null && priceMax != null
            ? `${priceMin}-${priceMax}`
            : priceMax != null
            ? `< ${priceMax}`
            : `> ${priceMin}`}
          <CloseIcon className="w-3 h-3" />
        </button>
      )}

      {/* View Mode Tabs — Map / Kuwait Finder / List all render into the
          same content-area slot (see viewMode branch in MapView/index.jsx).
          Replaces the old PACI toggle-switch + separate "Home" button
          with one 3-way control. */}
      <div className="flex items-center h-9 rounded-lg border border-brand/30 bg-white overflow-hidden">
        {[
          { key: 'map', label: language === 'ar' ? 'خريطة' : 'Map', Icon: MapIcon },
          { key: 'paci', label: language === 'ar' ? 'كويت فايندر' : 'Finder', Icon: LocationIcon },
          { key: 'list', label: language === 'ar' ? 'قائمة' : 'List', Icon: ListIcon }
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              onViewModeChange(key);
              if (location.pathname === '/admin') handleNavigation('/');
            }}
            className={`
              flex items-center gap-1.5 px-2 sm:px-3 h-full
              text-[11px] sm:text-xs font-medium whitespace-nowrap
              transition-colors duration-150
              ${viewMode === key && location.pathname !== '/admin' ? 'bg-brand text-white' : 'text-brand hover:bg-brand/5'}
            `}
          >
            <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* Saved Lands Toggle */}
      {onSavedToggle && (
        <button
          onClick={() => onSavedToggle(!showSavedOnly)}
          className={`
            flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg
            text-[11px] sm:text-xs font-medium
            transition-all duration-200
            ${showSavedOnly
              ? 'bg-brand text-white shadow-md'
              : 'bg-white text-brand border border-brand/30 hover:border-brand'
            }
          `}
        >
          <BookmarkIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" filled={showSavedOnly} />
          <span className="hidden sm:inline whitespace-nowrap">
            {language === 'ar' ? 'المحفوظة' : 'Saved'}
          </span>
        </button>
      )}

      {/* Admin Panel Button */}
      {isAuthenticated && (
        <button
          onClick={() => handleNavigation('/admin')}
          type="button"
          className={`
            flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg
            text-[11px] sm:text-xs font-medium
            transition-all duration-200
            ${isActive('/admin')
              ? 'bg-brand text-white shadow-md'
              : 'bg-white text-brand border border-brand/30 hover:border-brand'
            }
          `}
        >
          <AdminIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline whitespace-nowrap">
            {language === 'ar' ? 'الإدارة' : 'Admin'}
          </span>
        </button>
      )}
    </>
  );

  // Tabs for the mobile view-mode pill — same 3 destinations as the
  // desktop `controls` tabs above, styled as a compact icon-only segment.
  const viewModeTabs = [
    { key: 'map', label: language === 'ar' ? 'خريطة' : 'Map', Icon: MapIcon },
    { key: 'paci', label: language === 'ar' ? 'كويت فايندر' : 'Finder', Icon: LocationIcon },
    { key: 'list', label: language === 'ar' ? 'قائمة' : 'List', Icon: ListIcon }
  ];

  return (
    <header className="bg-[#f4f4f4] shadow-md z-[1300] flex-shrink-0">
      {/* Mobile layout (below sm): logo + sign-in/out on their own top row,
          then a single compact toolbar row underneath — a full-width search
          bar, one "Filters" button (opens a bottom sheet with categories/
          price/saved/admin, badged with the active count) and an icon-only
          Map/Finder/List pill. Deliberately separate markup from the
          desktop layout below rather than one shared structure bent
          sideways with breakpoint classes for every item. */}
      <div className="sm:hidden flex flex-col gap-2 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          {logo}
          {authButton}
        </div>

        {showSearchFilter && (
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand pointer-events-none" />
              <input
                type="text"
                placeholder={language === 'ar' ? 'ابحث بالكود...' : 'Search by code...'}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className={`
                  w-full h-10 pl-9 pr-3
                  bg-white border border-gray-200 rounded-full shadow-sm
                  font-cairo text-sm
                  focus:outline-none focus:border-brand
                  ${language === 'ar' ? 'text-right' : 'text-left'}
                `}
                dir={language === 'ar' ? 'rtl' : 'ltr'}
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded-full"
                >
                  <CloseIcon className="w-3.5 h-3.5 text-brand" />
                </button>
              )}
            </div>

            <button
              onClick={() => setFiltersSheetOpen(true)}
              type="button"
              className="relative flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm text-brand flex-shrink-0"
            >
              <FilterIcon className="w-4 h-4" />
              {mobileActiveFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-4 px-0.5 rounded-full bg-brand text-white text-[9px] font-bold">
                  {mobileActiveFilterCount}
                </span>
              )}
            </button>

            <div className="flex items-center h-10 rounded-full bg-gray-100 p-1 flex-shrink-0">
              {viewModeTabs.map(({ key, Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    onViewModeChange(key);
                    if (location.pathname === '/admin') handleNavigation('/');
                  }}
                  className={`
                    flex items-center justify-center w-8 h-8 rounded-full
                    transition-colors duration-150
                    ${viewMode === key && location.pathname !== '/admin' ? 'bg-white text-brand shadow-sm' : 'text-gray-500'}
                  `}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile "Filters" bottom sheet — categories, price, saved-only and
          admin all live here on mobile instead of in the toolbar row above.
          Same slide-up/backdrop/rounded-sheet treatment as the property
          details panel (see MapView/index.jsx) for a consistent app feel;
          z-[1400]/[1401] deliberately sit above the header's own z-[1300]
          since this is a full modal the user opened, not a passive peek. */}
      {filtersSheetOpen && (
        <>
          <div
            className="sm:hidden fixed inset-0 z-[1400] bg-black/40 animate-sheet-backdrop"
            onClick={() => setFiltersSheetOpen(false)}
          />
          <div className="sm:hidden fixed inset-x-0 bottom-0 z-[1401] max-h-[80vh] flex flex-col rounded-t-2xl shadow-2xl bg-white animate-sheet-up overflow-hidden">
            <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
              <div className="w-10 h-1.5 rounded-full bg-gray-300" />
            </div>

            <div className="px-4 pb-3 flex items-center justify-between flex-shrink-0 border-b border-gray-100">
              <h2 className="text-sm font-bold text-gray-800">
                {language === 'ar' ? 'الفلاتر' : 'Filters'}
              </h2>
              <button
                onClick={() => setFiltersSheetOpen(false)}
                className="p-1 rounded-lg hover:bg-gray-100"
              >
                <CloseIcon className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3 space-y-4">
              {/* Quick actions */}
              <div className="flex flex-wrap gap-2">
                {hasPriceFilter && (
                  <button
                    onClick={() => onPriceRangeChange && onPriceRangeChange({ min: null, max: null })}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand/10 text-brand text-xs font-medium"
                  >
                    {priceMin != null && priceMax != null
                      ? `${priceMin}-${priceMax}`
                      : priceMax != null
                      ? `< ${priceMax}`
                      : `> ${priceMin}`}
                    <CloseIcon className="w-3 h-3" />
                  </button>
                )}
                {onSavedToggle && (
                  <button
                    onClick={() => onSavedToggle(!showSavedOnly)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      showSavedOnly ? 'bg-brand text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <BookmarkIcon className="w-3.5 h-3.5" filled={showSavedOnly} />
                    {language === 'ar' ? 'المحفوظة' : 'Saved'}
                  </button>
                )}
                {isAuthenticated && (
                  <button
                    onClick={() => {
                      setFiltersSheetOpen(false);
                      handleNavigation('/admin');
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium"
                  >
                    <AdminIcon className="w-3.5 h-3.5" />
                    {language === 'ar' ? 'الإدارة' : 'Admin'}
                  </button>
                )}
              </div>

              {/* Categories */}
              {tags.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-500">
                      {language === 'ar' ? 'التصنيفات' : 'Categories'}
                    </span>
                    {selectedTags.length > 0 && (
                      <button
                        onClick={clearAllTags}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        {language === 'ar' ? 'مسح الكل' : 'Clear all'}
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {tags.filter(tag => tag.title !== 'All').map((tag) => (
                      <button
                        key={tag.id}
                        onClick={() => handleTagToggle(tag.title)}
                        className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-colors ${
                          selectedTags.includes(tag.title) ? 'bg-brand/10' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedTags.includes(tag.title)
                            ? 'bg-primary border-primary'
                            : 'border-gray-300'
                        }`}>
                          {selectedTags.includes(tag.title) && (
                            <CheckIcon className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span className="flex-1 text-left text-sm text-gray-700">
                          {tag.title}
                        </span>
                        <span className="text-xs text-gray-500 font-medium">
                          ({getTagCount(tag.id)})
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-gray-100 flex-shrink-0">
              <button
                onClick={() => setFiltersSheetOpen(false)}
                className="w-full py-2.5 rounded-lg bg-brand text-white text-sm font-semibold"
              >
                {language === 'ar' ? 'تطبيق' : 'Apply'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Desktop layout (sm and up): logo alone at the far left, a flexible
          blank spacer, then the controls and the auth button grouped
          together at the far right. */}
      <div className="hidden sm:flex items-center min-h-[44px] pr-5 px-4 md:px-8 py-2 md:py-4 gap-4">
        {logo}
        <div className="flex-1" />
        <div className="flex flex-row-reverse flex-wrap items-center justify-end gap-2 sm:gap-4 md:gap-6">
          {controls}
        </div>
        {authButton}
      </div>
    </header>
  );
}

export default Header;
